/**
 * Entity Type Guard Functions
 * 
 * These functions safely narrow types and eliminate the need for `as any` casts.
 * Each function checks if an unknown value matches a specific entity type.
 */

import type {
  ContactEntity,
  CompanyEntity,
  TicketEntity,
  TaskEntity,
  DealEntity,
  EntityWithCompany,
  EntityWithContact,
  EntityWithAssignment,
  EntityWithStatus,
  EntityWithPriority,
  EntityWithDates,
} from "@/types/entity-types";

/**
 * Check if a value is a ContactEntity
 * @param value - Unknown value to check
 * @returns True if value is a ContactEntity
 */
export function isContactEntity(value: unknown): value is ContactEntity {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.id === "string" &&
    (typeof obj.email === "string" ||
      typeof obj.phone === "string" ||
      typeof obj.first_name === "string" ||
      typeof obj.last_name === "string")
  );
}

/**
 * Check if a value is a CompanyEntity
 * @param value - Unknown value to check
 * @returns True if value is a CompanyEntity
 */
export function isCompanyEntity(value: unknown): value is CompanyEntity {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.id === "string" &&
    (typeof obj.name === "string" || typeof obj.company_name === "string")
  );
}

/**
 * Check if a value is a TicketEntity
 * @param value - Unknown value to check
 * @returns True if value is a TicketEntity
 */
export function isTicketEntity(value: unknown): value is TicketEntity {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.id === "string" &&
    (typeof obj.ticket_number === "string" || typeof obj.title === "string")
  );
}

/**
 * Check if a value is a TaskEntity
 * @param value - Unknown value to check
 * @returns True if value is a TaskEntity
 */
export function isTaskEntity(value: unknown): value is TaskEntity {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.id === "string" &&
    typeof obj.title === "string"
  );
}

/**
 * Check if a value is a DealEntity
 * @param value - Unknown value to check
 * @returns True if value is a DealEntity
 */
export function isDealEntity(value: unknown): value is DealEntity {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.id === "string" &&
    (typeof obj.name === "string" || typeof obj.stage === "string")
  );
}

/**
 * Check if a value has company information
 * @param value - Unknown value to check
 * @returns True if value has company_id or company_name
 */
export function hasCompanyInfo(value: unknown): value is EntityWithCompany {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.company_id === "string" ||
    typeof obj.company_name === "string" ||
    (typeof obj.company === "object" && obj.company !== null)
  );
}

/**
 * Check if a value has contact information
 * @param value - Unknown value to check
 * @returns True if value has contact_id or contact
 */
export function hasContactInfo(value: unknown): value is EntityWithContact {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.contact_id === "string" ||
    (typeof obj.contact === "object" && obj.contact !== null)
  );
}

/**
 * Check if a value has assignment information
 * @param value - Unknown value to check
 * @returns True if value has assigned_to
 */
export function hasAssignmentInfo(value: unknown): value is EntityWithAssignment {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.assigned_to === "string";
}

/**
 * Check if a value has status information
 * @param value - Unknown value to check
 * @returns True if value has status or state
 */
export function hasStatusInfo(value: unknown): value is EntityWithStatus {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.status === "string" || typeof obj.state === "string";
}

/**
 * Check if a value has priority information
 * @param value - Unknown value to check
 * @returns True if value has priority
 */
export function hasPriorityInfo(value: unknown): value is EntityWithPriority {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return typeof obj.priority === "string";
}

/**
 * Check if a value has date information
 * @param value - Unknown value to check
 * @returns True if value has any date fields
 */
export function hasDateInfo(value: unknown): value is EntityWithDates {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.created_at === "string" ||
    typeof obj.updated_at === "string" ||
    typeof obj.due_date === "string" ||
    typeof obj.close_date === "string"
  );
}

/**
 * Safely get a string property from an entity
 * @param entity - Entity to get property from
 * @param key - Property key
 * @returns Property value if it's a string, undefined otherwise
 */
export function getStringProperty(
  entity: Record<string, unknown>,
  key: string
): string | undefined {
  const value = entity[key];
  return typeof value === "string" ? value : undefined;
}

/**
 * Safely get a nested property from an entity
 * @param entity - Entity to get property from
 * @param path - Dot-separated path (e.g., "company.name")
 * @returns Property value if found, undefined otherwise
 */
export function getNestedProperty(
  entity: Record<string, unknown>,
  path: string
): unknown {
  const keys = path.split(".");
  let current: unknown = entity;

  for (const key of keys) {
    if (typeof current !== "object" || current === null) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }

  return current;
}

/**
 * Safely get a string from a nested property
 * @param entity - Entity to get property from
 * @param path - Dot-separated path (e.g., "company.name")
 * @returns Property value if it's a string, undefined otherwise
 */
export function getNestedStringProperty(
  entity: Record<string, unknown>,
  path: string
): string | undefined {
  const value = getNestedProperty(entity, path);
  return typeof value === "string" ? value : undefined;
}

/**
 * Extract company ID from an entity with multiple possible locations
 * @param entity - Entity to extract from
 * @returns Company ID if found, undefined otherwise
 */
export function extractCompanyId(entity: Record<string, unknown>): string | undefined {
  return (
    getStringProperty(entity, "company_id") ??
    getNestedStringProperty(entity, "company.id") ??
    getStringProperty(entity, "companyId")
  );
}

/**
 * Extract company name from an entity with multiple possible locations
 * @param entity - Entity to extract from
 * @returns Company name if found, undefined otherwise
 */
export function extractCompanyName(entity: Record<string, unknown>): string | undefined {
  return (
    getStringProperty(entity, "company_name") ??
    getNestedStringProperty(entity, "company.name") ??
    getStringProperty(entity, "companyName")
  );
}

/**
 * Extract contact ID from an entity with multiple possible locations
 * @param entity - Entity to extract from
 * @returns Contact ID if found, undefined otherwise
 */
export function extractContactId(entity: Record<string, unknown>): string | undefined {
  return (
    getStringProperty(entity, "contact_id") ??
    getNestedStringProperty(entity, "contact.id") ??
    getStringProperty(entity, "contactId") ??
    getStringProperty(entity, "id")
  );
}

/**
 * Extract contact ID only from explicit contact fields (never falls back to entity id).
 * Use this when the record itself may not be a contact row (e.g. tickets/tasks/messages).
 */
export function extractExplicitContactId(entity: Record<string, unknown>): string | undefined {
  return (
    getStringProperty(entity, "contact_id") ??
    getNestedStringProperty(entity, "contact.id") ??
    getStringProperty(entity, "contactId")
  );
}

/**
 * Extract contact name from an entity with multiple possible locations
 * @param entity - Entity to extract from
 * @returns Contact name if found, undefined otherwise
 */
export function extractContactName(entity: Record<string, unknown>): string | undefined {
  return (
    getStringProperty(entity, "name") ??
    getNestedStringProperty(entity, "contact.name") ??
    getStringProperty(entity, "contactName")
  );
}
